/**
 * Process-wide runtime context shared by all Flash classes (the "VM"): clock, frame phase,
 * broadcast listener lists, frame-lifecycle queues, timers, sound channels, error policy.
 *
 * A default Runtime exists from module load so that BitmapData/geom/etc. work without a player.
 * `new FlashPlayer()` adopts the current runtime if it has no player yet (so objects created
 * before the player keep their ENTER_FRAME registrations), otherwise it creates a fresh one.
 *
 * This module has no runtime imports of display classes (only types) to avoid ESM cycles.
 */
import type { Platform } from '../platform/types';

export type FramePhase = 'idle' | 'enter' | 'construct' | 'scripts' | 'exit';

/** Event types that Flash Player broadcasts to every registered listener (no display-list path). */
export const BROADCAST_EVENTS = new Set(['enterFrame', 'exitFrame', 'frameConstructed', 'render', 'activate', 'deactivate']);

interface BroadcastEntry {
  /** Strong reference while the object has at least one listener for the event type. */
  strong: any;
  weak: WeakRef<any>;
}

export type ErrorPolicy = 'log' | 'throw' | 'silent';

export class Runtime {
  platform: Platform | null = null;
  /** The owning FlashPlayer (typed loosely to avoid an import cycle). */
  player: any = null;
  stage: any = null;
  phase: FramePhase = 'idle';
  frameRate = 50;

  /** Time origin for getTimer(). */
  startTime = 0;
  /** Fallback clock when no platform is attached (advanced by FlashPlayer without platform clock). */
  virtualTime = 0;

  /** Broadcast registrations in first-registration order (Flash semantics). */
  readonly broadcast = new Map<string, BroadcastEntry[]>();

  /** Timeline-placed objects not yet "constructed" (named-property binding + ADDED events pending). */
  readonly unconstructed = new Set<any>();
  /** MovieClips that have a frame script to run for their current frame. */
  readonly pendingScripts = new Set<any>();
  /** Clips created since the last frame-script phase: they do not advance before running frame 1. */
  fresh: any[] = [];

  readonly timers = new Set<any>();
  readonly channels = new Set<any>();

  /** What happens to exceptions thrown by listeners / frame scripts (Flash release player: swallow). */
  errorPolicy: ErrorPolicy = 'log';
  /** Caught errors (most recent last, capped). */
  readonly errors: unknown[] = [];
  onError: ((err: unknown, where: string) => void) | null = null;

  /**
   * Host text filter: every string that becomes visible in a TextField (the initial text of an asset's edittext and
   * every assignment to `text` / `htmlText`) passes through it. The port uses it for localization - the game's own
   * strings are Russian and stay canonical for game logic, only what is shown is translated.
   */
  localize: ((text: string) => string) | null = null;
  /**
   * Host variant of the asset art: a character with a variant of this name in its bundle's `localized` table
   * (lettering drawn into a shape, redrawn per UI language) is shown as that variant. Null: the original art.
   */
  assetVariant: string | null = null;

  mouseHidden = false;
  /** Mouse.cursor value ("auto", "arrow", "button", "hand", "ibeam"). */
  mouseCursor = 'auto';

  instanceCounter = 0;
  /** Incremented whenever display state that affects rendering may have changed. */
  renderVersion = 0;
  /**
   * PORT (damage rendering): display objects changed since the renderer last took them (each once, its `$dflags`
   * say how), and BitmapData whose pixels changed; null while no renderer tracks changes (render-data trackChanges).
   */
  changes: any[] | null = null;
  changedBitmaps: any[] | null = null;

  now(): number {
    return this.platform ? this.platform.now() : this.virtualTime;
  }

  getTimer(): number {
    return Math.floor(this.now() - this.startTime);
  }

  reportError(err: unknown, where: string): void {
    this.errors.push(err);
    if (this.errors.length > 100) this.errors.shift();
    if (this.onError) this.onError(err, where);
    if (this.errorPolicy === 'throw') throw err;
    if (this.errorPolicy === 'log') {
      // eslint-disable-next-line no-console
      console.error(`[flash] uncaught error in ${where}:`, err);
    }
  }

  // ------------------------------------------------------------------ broadcast list
  registerBroadcast(obj: any, type: string): void {
    if (!BROADCAST_EVENTS.has(type)) return;
    const reg: Record<string, BroadcastEntry> = obj.$bcast || (obj.$bcast = Object.create(null));
    const existing = reg[type];
    if (existing && existing.weak.deref() === obj) {
      existing.strong = obj;
      return;
    }
    const entry: BroadcastEntry = { strong: obj, weak: new WeakRef(obj) };
    reg[type] = entry;
    let list = this.broadcast.get(type);
    if (!list) this.broadcast.set(type, (list = []));
    list.push(entry);
  }

  /** Called when an object no longer has listeners of `type`: keep its slot, but only weakly. */
  releaseBroadcast(obj: any, type: string): void {
    const entry = obj.$bcast?.[type] as BroadcastEntry | undefined;
    if (entry) entry.strong = null;
  }

  /**
   * Dispatch a broadcast event to every registered object (registration order; objects registered
   * during the broadcast are not called; target phase only). `filter` restricts receivers.
   */
  broadcastEvent(type: string, makeEvent: () => any, filter?: (obj: any) => boolean): void {
    const list = this.broadcast.get(type);
    if (!list || list.length === 0) return;
    const n = list.length;
    let dead = 0;
    for (let i = 0; i < n; i++) {
      const entry = list[i];
      const obj = entry.strong ?? entry.weak.deref();
      if (!obj) {
        dead++;
        continue;
      }
      if (!entry.strong) continue;
      if (filter && !filter(obj)) continue;
      obj.$dispatchBroadcast(makeEvent());
    }
    if (dead > 0) {
      const alive = list.filter((e) => e.strong || e.weak.deref());
      list.length = 0;
      list.push(...alive);
    }
  }
}

let current = new Runtime();

/** The current runtime (VM). */
export function rt(): Runtime {
  return current;
}

export function setRuntime(r: Runtime): void {
  current = r;
}

/** Create and install a brand-new runtime (tests). */
export function resetRuntime(): Runtime {
  current = new Runtime();
  return current;
}
